import {
  AccessRoleService,
  BusinessService,
  ErrorFromResponse,
  OutletService,
  SubscriptionService,
  useCurrentUser,
  useCurrentUserAccessRoles,
  useSubscriptionPlans,
} from '@billx/api';
import type { BusinessScale, SubscriptionPlan } from '@billx/types';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
  useTheme,
} from '@mui/material';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useNotification } from '@/hooks';

import { CustomDialog } from './CustomDialog';
import { BusinessInfoForm, OutletInfoForm, PlanSelectionForm } from './forms';
import { type CreateBusinessData, createBusinessSchema } from './forms';
const steps = ['Business', 'Plan Selection', 'Outlet'];

const stepMetadata = [
  {
    title: 'Business Information',
    subtitle: 'Tell us about your business so we can customize your experience',
  },
  {
    title: 'Choose Your Plan',
    subtitle: 'Select a plan that fits your business needs',
  },
  {
    title: 'Outlet Information',
    subtitle: 'Set up your first outlet for your business',
  },
];

interface CreateBusinessModalProps {
  open: boolean;
  onClose: () => void;
}

export const CreateBusinessModal: React.FC<CreateBusinessModalProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const { showNotification } = useNotification();
  const { currentUser, updateUserPreference } = useCurrentUser();
  const { refetch: refetchCurrentUserAccessRoles } =
    useCurrentUserAccessRoles();
  const { plans = [], isLoading: isPlansLoading } = useSubscriptionPlans();
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<
    SubscriptionPlan | undefined
  >();

  const methods = useForm<CreateBusinessData>({
    resolver: zodResolver(createBusinessSchema),
    mode: 'onChange', // Validate on change for better UX
    defaultValues: {
      business: {
        name: '',
        legalName: '',
        type: '',
        scale: 'small',
      },
      outlet: {
        name: '',
        street: '',
        city: '',
        state: '',
        country: '',
        zipcode: '',
        // outletEmail: '',
        // outletPhoneCode: DEFAULT_COUNTRY.phoneCode,
        // outletPhoneNumber: '',
      },
    },
  });

  const handleNext = async () => {
    // Trigger validation for the current step
    let isValid = false;

    if (activeStep === 0) {
      isValid = await methods.trigger('business');
    } else if (activeStep === 1) {
      isValid = await methods.trigger('outlet');
    }

    if (isValid) {
      if (activeStep === steps.length - 1) {
        handleSubmit();
      } else {
        setActiveStep((prevStep) => prevStep + 1);
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      // Reset form state
      setActiveStep(0);
      methods.reset();
      onClose();
    }
  };

  const handleSubmit = async () => {
    const formData = methods.getValues();
    setIsSubmitting(true);

    try {
      // Create business
      const businessResponse = await BusinessService.createBusiness({
        name: formData.business.name,
        legalName: formData.business.legalName,
        type: formData.business.type,
        scale: formData.business.scale as BusinessScale,
      });

      if (!businessResponse.success) {
        throw new ErrorFromResponse(businessResponse);
      }

      const business = businessResponse.data;

      if (selectedPlan) {
        const response = await SubscriptionService.createSubscription(
          business.id,
          selectedPlan
        );
        if (!response.success) {
          showNotification({
            message: response.message || 'Failed to create subscription',
            severity: 'error',
            duration: 5000,
          });
          return;
        }
      }

      // Create user role as owner
      if (currentUser) {
        const roleResponse = await AccessRoleService.grantAccess({
          userId: currentUser.id,
          businessId: business.id,
          role: 'owner',
        });

        if (!roleResponse.success) {
          throw new Error(roleResponse.message || 'Failed to create user role');
        }
      }

      const outletResponse = await OutletService.createOutlet({
        businessId: business.id,
        name: formData.outlet.name,
        address: {
          street: formData.outlet.street,
          city: formData.outlet.city,
          state: formData.outlet.state,
          country: formData.outlet.country,
          zipcode: formData.outlet.zipcode,
        },
      });

      if (!outletResponse.success) {
        throw new ErrorFromResponse(outletResponse);
      }

      await updateUserPreference.mutateAsync({
        selectedBusinessId: business.id,
        selectedOutletId: outletResponse.data.id,
      });

      await refetchCurrentUserAccessRoles();

      showNotification({
        message: 'Business created successfully!',
        severity: 'success',
        duration: 1000,
      });

      // Close modal
      handleClose();
    } catch (error) {
      console.error('Error creating business:', error);
      showNotification({
        message:
          error instanceof Error ? error.message : 'Failed to create business',
        severity: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <BusinessInfoForm disabled={isSubmitting} />;
      case 1:
        return (
          <PlanSelectionForm
            plans={plans}
            isPlansLoading={isPlansLoading}
            activePlanId={selectedPlan?.id}
            onPlanSelect={(plan) => setSelectedPlan(plan)}
          />
        );
      case 3:
        return <OutletInfoForm disabled={isSubmitting} fieldPrefix="outlet" />;
      default:
        return null;
    }
  };

  const currentStepMeta = stepMetadata[activeStep];
  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === steps.length - 1;

  return (
    <CustomDialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        elevation: 0,
        sx: {
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 3,
        },
      }}
    >
      <FormProvider {...methods}>
        {/* Fixed Header */}
        <DialogTitle
          sx={{
            px: { xs: 2, md: 4 },
            pt: 3,
            pb: 0,
            borderBottom: `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
          }}
        >
          <Typography variant="h5" component="h1" gutterBottom>
            {currentStepMeta.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {currentStepMeta.subtitle}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Step {activeStep + 1} of {steps.length}
            </Typography>
            <Box sx={{ flex: 1 }}>
              <LinearProgress
                variant="determinate"
                value={(activeStep / (steps.length - 1)) * 100}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(0, 0, 0, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    background:
                      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  },
                }}
              />
            </Box>
          </Box>
        </DialogTitle>

        {/* Scrollable Content */}
        <DialogContent
          sx={{
            flex: 1,
            overflow: 'auto',
          }}
        >
          <Box sx={{ py: 3 }}>{renderStepContent(activeStep)}</Box>
        </DialogContent>

        {/* Fixed Footer */}
        <DialogActions
          sx={{
            px: { xs: 2, md: 4 },
            py: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowLeftIcon size={18} />}
            onClick={handleBack}
            disabled={isFirstStep || isSubmitting}
          >
            Back
          </Button>
          <Button
            variant="contained"
            endIcon={<ArrowRightIcon size={18} />}
            onClick={handleNext}
            disabled={isSubmitting}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
            }}
          >
            {isSubmitting
              ? 'Creating...'
              : isLastStep
                ? 'Create Business'
                : 'Next'}
          </Button>
        </DialogActions>
      </FormProvider>
    </CustomDialog>
  );
};
